import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  Button,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavMenu,
  SideNavMenuItem,
  SkeletonPlaceholder,
  Loading,
} from "@carbon/react";
import { Checkmark } from "@carbon/icons-react";
import { ArrowLeft, ArrowUp, Fade } from "@carbon/icons-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { instituteArticlesData } from "../../../../instituteArticles/instituteArticlesData";
import InstituteArticleQuestionTile from "../../../../components/instituteArticleQuestionTile/InstituteArticleQuestionTile";
import { queryClient } from "../../../../";
import { useSetArticleAnswers } from "../../../../hooks/useSetArticleAnswers";

export default function ArticlePage() {
  const { instituteArticles, isInstitutePending } = useOutletContext();
  const { title } = useParams();
  const { i18n, t } = useTranslation();
  const [html, setHtml] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const [activeSideNav, setActiveSideNav] = useState("");
  const [userAnswers, setUserAnswers] = useState([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isHtmlLoading, setIsHtmlLoading] = useState(true);

  const currentArticle = instituteArticlesData.find(
    (article) => article.name === title
  );
  const currentAnswers = instituteArticles.find((item) => item.name === title);
  const currentRevision = currentArticle?.revision ?? "";
  const navigationItems = currentArticle?.navigation ?? [];
  const questions = currentArticle?.questions ?? [];
  const answers = currentAnswers?.questions ?? [];

  const mutationAnswer = useSetArticleAnswers();

  useEffect(() => {
    if (html && location.hash) {
      const hash = location.hash;
      setTimeout(() => {
        const el = document.getElementById(hash.replace("#", ""));
        if (el) {
          if (hash === "#questions") {
            const offset = 96;
            const elementPosition =
              el.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
              top: elementPosition - offset,
              behavior: "smooth",
            });
          } else {
            el.scrollIntoView({ behavior: "smooth" });
          }
          setActiveSideNav(hash);
        }
      }, 200);
    }
  }, [html, location.hash]);

  useEffect(() => {
    const lang = i18n.language.slice(0, 2);

    if (!currentArticle) {
      setHtml(`<p>${t("main.institute.notFound")}</p>`);
      setIsHtmlLoading(false);
      return;
    }

    setIsHtmlLoading(true);

    fetch(`/institute_articles/${title}/${lang}.html`)
      .then((res) => res.text())
      .then((data) => {
        setHtml(data);
        setIsHtmlLoading(false);
      })
      .catch(() => {
        setHtml(`<p>${t("main.institute.notFound")}</p>`);
        setIsHtmlLoading(false);
      });
  }, [title, i18n.language, t, currentArticle]);

  useEffect(() => {
    if (!html) return;

    const headings = document.querySelectorAll(
      ".article_page_main [id], #questions"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) {
          const idWithHash = `#${visible.target.id}`;
          setActiveSideNav(idWithHash);
          window.history.replaceState(null, "", idWithHash);
        }
      },
      {
        rootMargin: "-47px 0px -90% 0px",
        threshold: 0,
      }
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => {
      headings.forEach((heading) => observer.unobserve(heading));
    };
  }, [html]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleNavClick = (anchor) => {
    setActiveSideNav(anchor);
    const target = document.getElementById(anchor.replace("#", ""));
    if (target) {
      if (anchor === "questions") {
        const offset = 96;
        const elementPosition =
          target.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: "smooth",
        });
      } else {
        target.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleUserAnswer = (newAnswer) => {
    setUserAnswers((prev) => {
      const exists = prev.find((a) => a.id === newAnswer.id);
      if (exists) {
        return prev.map((a) => (a.id === newAnswer.id ? newAnswer : a));
      }
      return [...prev, newAnswer];
    });
  };

  const handleAnswerAgain = () => {
    setUserAnswers([]);
    setIsRetrying(true);
    setTimeout(() => {
      const target = document.getElementById("questions");
      if (target) {
        const offset = 96;
        const elementPosition =
          target.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: "smooth",
        });
      }
    }, 0);
  };

  const handleAnswer = () => {
    const completedArticle = userAnswers.find((item) => !item.completed);
    mutationAnswer.mutate(
      {
        name: title,
        revision: currentRevision,
        completed: !completedArticle,
        questions: userAnswers,
      },
      {
        onSuccess: (data) => {
          setUserAnswers([]);
          setIsRetrying(false);
          queryClient.invalidateQueries({ queryKey: ["institute-articles"] });
        },
        onError: (error) => {
          navigate("/error", {
            state: {
              code: error.code,
              message: error.message,
            },
          });
        },
      }
    );
  };

  return (
    <>
      <div className="article_page_with_nav">
        {navigationItems.length > 0 && (
          <SideNav
            isRail
            enterDelayMs={100}
            className="article_page_sidenav"
            aria-label=""
            aria-labelledby=""
          >
            <SideNavItems>
              {navigationItems.map((item) =>
                item.children?.length ? (
                  <SideNavMenu
                    title={t(item.label)}
                    key={item.anchor}
                    isActive={
                      item.anchor === activeSideNav ||
                      item.children.some(
                        (child) => child.anchor === activeSideNav
                      )
                    }
                  >
                    {item.children.map((child) => (
                      <SideNavMenuItem
                        href={child.anchor}
                        key={child.anchor}
                        isActive={child.anchor === activeSideNav}
                        onClick={() => handleNavClick(child.anchor)}
                      >
                        {t(child.label)}
                      </SideNavMenuItem>
                    ))}
                  </SideNavMenu>
                ) : (
                  <SideNavLink
                    href={item.anchor}
                    key={item.anchor}
                    isActive={item.anchor === activeSideNav}
                    onClick={() => handleNavClick(item.anchor)}
                    renderIcon={Fade}
                  >
                    {t(item.label)}
                  </SideNavLink>
                )
              )}
            </SideNavItems>
          </SideNav>
        )}
      </div>
      <div className="article_page">
        <Button
          onClick={() => navigate("/institute")}
          renderIcon={ArrowLeft}
          hasIconOnly
          iconDescription={`${t("main.buttons.back")}`}
          kind="ghost"
          tooltipPosition="top"
          tooltipAlignment="center"
          size="md"
          className="article_page_arrow_back"
        >
          Back
        </Button>
        {isHtmlLoading ? (
          <Loading withOverlay={false} description="" />
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: html }}
            className="article_page_main"
          />
        )}
      </div>
      {!isHtmlLoading && !isInstitutePending && questions.length > 0 ? (
        <div className="article_page_questions" id="questions">
          {questions.map((question) => (
            <InstituteArticleQuestionTile
              key={question.id}
              question={question}
              answer={answers.find((item) => item.id === question.id)}
              articleId={currentArticle.id}
              onAnswer={handleUserAnswer}
              isRetrying={isRetrying}
            />
          ))}
          {answers.length && !currentAnswers.completed && !isRetrying ? (
            <Button
              className="article_page_questions_submit"
              onClick={handleAnswerAgain}
            >
              {t("main.institute.question_answerAgain")}
            </Button>
          ) : answers.length && currentAnswers.completed ? null : (
            <Button
              renderIcon={Checkmark}
              className="article_page_questions_submit"
              onClick={handleAnswer}
              disabled={userAnswers.length < questions.length}
            >
              {t("main.institute.question_submit")}
            </Button>
          )}
        </div>
      ) : isInstitutePending ? (
        <SkeletonPlaceholder />
      ) : null}
      <div className="article_page_scroll_up">
        <Button
          renderIcon={ArrowUp}
          hasIconOnly
          iconDescription={`${t("main.buttons.up")}`}
          kind="ghost"
          tooltipPosition="top"
          size="lg"
          className="article_page_arrow_up"
          onClick={scrollToTop}
        ></Button>
      </div>
    </>
  );
}
